import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, Laptop, ListPlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { DateTime } from 'luxon';
import { CLASS_LABELS, type AgentDeviceDto, type ProductivityClass, type ProductivityDayDto, type ProductivityRuleDto, type ScreenshotDto, type ScreenshotListDto, type UnclassifiedDto } from '@workforce/shared';
import { Camera } from 'lucide-react';
import { formatMinutes, t } from '@/lib/i18n';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTz } from '@/lib/timezone';
import { useDepartments, useEmployeeOptions } from '@/lib/queries';
import { useCurrentPeriod } from '@/lib/periods';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, Notice, PageHeader, Select, Spinner, Tabs } from '@/components/ui';
import { ClassBadge, FlaggedList, Legend, ProductivityTable, ScreenshotGallery } from '@/components/ProductivityParts';
import { RANGE_OPTIONS, rangeFor, type RangePreset } from '@/components/AttendanceTable';
import { DailyReportTab, DashboardTab, PersonDaysChart } from './ProductivityDashboard';

type Tab = 'dashboard' | 'report' | 'employee' | 'unclassified' | 'rules' | 'devices';

export function ProductivityPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab = (raw === 'day' ? 'report' : (raw as Tab)) || 'dashboard';
  const setTab = (t: Tab) => setParams((p) => { p.set('tab', t); return p; }, { replace: true });
  const admin = can('productivity.rules');
  const tabs: { value: Tab; label: string }[] = [
    { value: 'dashboard', label: t('Dashboard') },
    { value: 'report', label: t('Daily report') },
    { value: 'employee', label: t('By employee') },
    ...(admin
      ? [
          { value: 'unclassified' as Tab, label: t('Not classified') },
          { value: 'rules' as Tab, label: t('Apps & websites') },
          { value: 'devices' as Tab, label: t('Computers') },
        ]
      : []),
  ];
  return (
    <div>
      <div className="print:hidden">
      <PageHeader
        title={t('Productivity')}
        subtitle={t('From clock-in to clock-out: time in productive and unproductive apps and websites, and idle time. No keystrokes are recorded.')}
      />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      </div>
      {tab === 'dashboard' && <DashboardTab onEmployee={(id) => setParams({ tab: 'employee', employee: id })} onDay={(date) => setParams({ tab: 'report', date })} />}
      {tab === 'report' && <DailyReportTab onEmployee={(id) => setParams({ tab: 'employee', employee: id })} />}
      {tab === 'employee' && <EmployeeTab />}
      {tab === 'unclassified' && admin && <UnclassifiedTab />}
      {tab === 'rules' && admin && <RulesTab />}
      {tab === 'devices' && admin && <DevicesTab />}
    </div>
  );
}

function EmployeeTab() {
  const [params, setParams] = useSearchParams();
  const employeeId = params.get('employee') ?? '';
  const { companyZone } = useTz();
  const opts = useEmployeeOptions();
  const periods = useCurrentPeriod();
  const [preset, setPreset] = useState<RangePreset>('week');
  const [custom, setCustom] = useState(() => rangeFor('week', companyZone));
  const range = rangeFor(preset, companyZone, custom, periods.data);
  const q = useQuery({
    queryKey: ['productivity', 'employee', employeeId, range.from, range.to],
    queryFn: () => api.get<ProductivityDayDto[]>(`/productivity/employees/${employeeId}/days${qs(range)}`),
    enabled: !!employeeId,
  });
  const qc = useQueryClient();
  const shots = useQuery({
    queryKey: ['productivity', 'employee-shots', employeeId, range.from, range.to],
    queryFn: () => api.get<ScreenshotListDto>(`/productivity/employees/${employeeId}/screenshots${qs(range)}`),
    enabled: !!employeeId,
  });
  const [deletingShot, setDeletingShot] = useState<ScreenshotDto | null>(null);
  const delShot = useMutation({
    mutationFn: (id: string) => api.del(`/productivity/screenshots/${id}`),
    onSuccess: () => { setDeletingShot(null); void qc.invalidateQueries({ queryKey: ['productivity', 'employee-shots'] }); },
  });
  const empName = opts.data?.find((o) => o.id === employeeId)?.fullName ?? '';
  const totals = useMemo(() => {
    const t = { tracked: 0, productive: 0, unproductive: 0, idle: 0, unknown: 0 };
    for (const d of q.data ?? []) {
      t.tracked += d.trackedMinutes;
      t.productive += d.productiveMinutes;
      t.unproductive += d.unproductiveMinutes;
      t.idle += d.idleMinutes;
      t.unknown += d.unknownMinutes;
    }
    return t;
  }, [q.data]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('Employee')} className="min-w-64">
          <Select value={employeeId} onChange={(e) => setParams((p) => { p.set('employee', e.target.value); return p; }, { replace: true })}>
            <option value="">{t('Choose an employee…')}</option>
            {opts.data?.map((o) => (
              <option key={o.id} value={o.id}>{o.fullName} ({o.employeeCode})</option>
            ))}
          </Select>
        </Field>
        <Field label={t('Period')}>
          <Select value={preset} onChange={(e) => setPreset(e.target.value as RangePreset)}>
            {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
          </Select>
        </Field>
        {preset === 'custom' && (
          <>
            <Field label={t('From')}><Input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} /></Field>
            <Field label={t('To')}><Input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} /></Field>
          </>
        )}
      </div>
      {!employeeId ? (
        <Card><EmptyState title={t('Choose an employee')} /></Card>
      ) : (
        <>
          <ErrorBanner error={q.error} />
          {q.data && (
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-5">
              {[
                [t('Tracked'), formatMinutes(totals.tracked), ''],
                [t('Productive'), formatMinutes(totals.productive), 'text-emerald-700'],
                [t('Unproductive'), formatMinutes(totals.unproductive), 'text-red-600'],
                [t('Not classified'), formatMinutes(totals.unknown), 'text-amber-700'],
                [t('Idle'), formatMinutes(totals.idle), 'text-slate-500'],
              ].map(([l, v, c]) => (
                <div key={l} className="bg-surface px-3 py-2.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{l}</p>
                  <p className={`mt-0.5 text-sm font-semibold tabular-nums ${c}`}>{v}</p>
                </div>
              ))}
            </div>
          )}
          {q.data && <PersonDaysChart days={q.data} />}
          <Card bodyClass="p-0">{q.isLoading ? <Spinner /> : q.data && <ProductivityTable days={q.data} />}</Card>
          {shots.data && (!!shots.data.screenshots.length || !!shots.data.flagged?.length) && (
            <Card title={<span className="flex items-center gap-2"><Camera className="h-4 w-4 text-brand-600" /> {t('Screenshots')}</span>}>
              <div className="space-y-4">
                {!!shots.data.flagged?.length && <FlaggedList flagged={shots.data.flagged} timezone={companyZone} />}
                <ErrorBanner error={delShot.error} />
                <ScreenshotGallery screenshots={shots.data.screenshots} imageBase="/api/productivity/screenshots" timezone={companyZone} onDelete={setDeletingShot} />
                <p className="text-xs text-slate-500">{t('{name} can see these same screenshots but cannot delete them — only an administrator can. Every time you open one is recorded in the activity log.', { name: empName })}</p>
              </div>
            </Card>
          )}
        </>
      )}
      <ConfirmDialog
        open={!!deletingShot}
        onClose={() => setDeletingShot(null)}
        onConfirm={() => deletingShot && delShot.mutate(deletingShot.id)}
        title={t('Delete this screenshot?')}
        message={t('The screenshot is removed for everyone and cannot be recovered. Recorded working time is not changed.')}
        confirmLabel={t('Delete')}
        danger
        loading={delShot.isPending}
        error={delShot.error}
      />
    </div>
  );
}

function UnclassifiedTab() {
  const qc = useQueryClient();
  const [days, setDays] = useState(7);
  const to = DateTime.now().toISODate()!;
  const from = DateTime.now().minus({ days: days - 1 }).toISODate()!;
  const q = useQuery({ queryKey: ['productivity', 'unclassified', days], queryFn: () => api.get<UnclassifiedDto[]>(`/productivity/unclassified?from=${from}&to=${to}`) });
  const add = useMutation({
    mutationFn: (v: { item: UnclassifiedDto; classification: ProductivityClass }) =>
      api.post('/productivity/rules', { matchType: v.item.matchType, pattern: v.item.pattern, label: v.item.label !== v.item.pattern ? v.item.label : null, classification: v.classification }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['productivity'] }),
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field label={t('Used in the last')}>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>{t('{n} days', { n: 7 })}</option>
            <option value={30}>{t('{n} days', { n: 30 })}</option>
            <option value={90}>{t('{n} days', { n: 90 })}</option>
          </Select>
        </Field>
        <p className="max-w-md text-xs text-slate-500">{t('Classifying an item applies to all days, including past ones (closed reporting periods keep their frozen figures).')}</p>
      </div>
      <ErrorBanner error={add.error ?? q.error} />
      <Card bodyClass="p-0">
        {q.isLoading ? (
          <Spinner />
        ) : q.data?.length ? (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">{t('App / website')}</th>
                <th className="px-4 py-2 text-end">{t('Time')}</th>
                <th className="hidden px-4 py-2 text-end sm:table-cell">{t('People')}</th>
                <th className="px-4 py-2 text-end">{t('Classify as')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((u) => (
                <tr key={`${u.matchType}:${u.pattern}`} className="border-b border-slate-100">
                  <td className="px-4 py-2">
                    <span className="font-medium">{u.label}</span>
                    <span className="ms-2 text-xs text-slate-400">{u.matchType === 'domain' ? t('website') : t('app')} · {u.pattern}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-end tabular-nums">{formatMinutes(u.minutes)}</td>
                  <td className="hidden px-4 py-2 text-end tabular-nums sm:table-cell">{u.employees}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-end">
                    <div className="inline-flex gap-1">
                      {(['productive', 'neutral', 'unproductive'] as const).map((c) => (
                        <Button key={c} size="sm" variant={c === 'productive' ? 'primary' : c === 'unproductive' ? 'danger' : 'secondary'} disabled={add.isPending} onClick={() => add.mutate({ item: u, classification: c })}>
                          {t(CLASS_LABELS[c])}
                        </Button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState title={t('Everything is classified')} description={t('New apps and websites appear here as the engineers use them.')} />
        )}
      </Card>
    </div>
  );
}

function RulesTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['productivity', 'rules'], queryFn: () => api.get<ProductivityRuleDto[]>('/productivity/rules') });
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<ProductivityRuleDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ProductivityRuleDto | null>(null);
  const starter = useMutation({ mutationFn: () => api.post<{ added: number }>('/productivity/rules/starter', {}), onSuccess: () => qc.invalidateQueries({ queryKey: ['productivity'] }) });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/productivity/rules/${id}`),
    onSuccess: () => { setDeleting(null); qc.invalidateQueries({ queryKey: ['productivity'] }); },
  });
  const rows = (q.data ?? []).filter((r) => !filter || `${r.label ?? ''} ${r.pattern} ${r.category ?? ''} ${r.employeeName ?? ''}`.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Input placeholder={t('Search… e.g. revit')} value={filter} onChange={(e) => setFilter(e.target.value)} className="w-64" aria-label={t('Search rules')} />
        <div className="flex gap-2">
          <Button variant="secondary" icon={<ListPlus className="h-4 w-4" />} loading={starter.isPending} onClick={() => starter.mutate()}>
            {t('Load starter list')}
          </Button>
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>{t('Add')}</Button>
        </div>
      </div>
      {starter.data && <Notice tone="green">{starter.data.added ? t('{n} apps and websites added from the starter list.', { n: starter.data.added }) : t('The starter list is already loaded.')}</Notice>}
      <ErrorBanner error={q.error} />
      <Card bodyClass="p-0">
        {q.isLoading ? (
          <Spinner />
        ) : rows.length ? (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">{t('Name')}</th>
                <th className="hidden px-4 py-2 sm:table-cell">{t('Matches')}</th>
                <th className="px-4 py-2">{t('Class')}</th>
                <th className="hidden px-4 py-2 md:table-cell">{t('Applies to')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-medium">
                    {r.label ?? r.pattern}
                    {r.note && <span className="ms-2 text-xs font-normal text-slate-400">{r.note}</span>}
                    {r.category && <p className="text-xs font-normal text-slate-400">{r.category}</p>}
                  </td>
                  <td className="hidden px-4 py-2 font-mono text-xs text-slate-500 sm:table-cell">{r.matchType === 'domain' ? t('website {pattern}', { pattern: r.pattern }) : r.matchType === 'title' ? t('title contains {pattern}', { pattern: r.pattern }) : t('app {pattern}', { pattern: r.pattern })}</td>
                  <td className="px-4 py-2"><ClassBadge c={r.classification} /></td>
                  <td className="hidden px-4 py-2 text-slate-500 md:table-cell">
                    {r.employeeName ? <span className="font-medium text-brand-700">{t('Only {name}', { name: r.employeeName })}</span> : r.departmentName ? t('{name} only', { name: r.departmentName }) : t('Everyone')}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-end">
                    <Button size="sm" variant="ghost" icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => setEditing(r)} aria-label={t('Edit {name}', { name: r.label ?? r.pattern })} />
                    <Button size="sm" variant="ghost" icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => setDeleting(r)} aria-label={t('Delete {name}', { name: r.label ?? r.pattern })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            title={filter ? t('No match') : t('No apps or websites classified yet')}
            description={t('Load the starter list for an engineering office, then adjust it — or add your own.')}
          />
        )}
      </Card>
      {editing && <RuleModal rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && del.mutate(deleting.id)}
        title={t('Remove this rule?')}
        message={t('{name} will count as “not classified” again (past days too, except closed periods).', { name: deleting?.label ?? deleting?.pattern })}
        confirmLabel={t('Remove')}
        danger
        loading={del.isPending}
        error={del.error}
      />
    </div>
  );
}

function RuleModal({ rule, onClose }: { rule: ProductivityRuleDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const depts = useDepartments();
  const people = useEmployeeOptions();
  const [f, setF] = useState({
    matchType: rule?.matchType ?? 'app',
    pattern: rule?.pattern ?? '',
    label: rule?.label ?? '',
    classification: rule?.classification ?? ('productive' as ProductivityClass),
    scope: rule?.employeeId ? `e:${rule.employeeId}` : rule?.departmentId ? `d:${rule.departmentId}` : '',
    category: rule?.category ?? '',
    note: rule?.note ?? '',
  });
  const categories = useQuery({ queryKey: ['productivity', 'rules'], queryFn: () => api.get<ProductivityRuleDto[]>('/productivity/rules') });
  const categoryList = [...new Set((categories.data ?? []).map((r) => r.category).filter(Boolean) as string[])].sort();
  const save = useMutation({
    mutationFn: () => {
      const { scope, ...rest } = f;
      const body = {
        ...rest,
        label: f.label || null,
        departmentId: scope.startsWith('d:') ? scope.slice(2) : null,
        employeeId: scope.startsWith('e:') ? scope.slice(2) : null,
        category: f.category.trim() || null,
        note: f.note || null,
      };
      return rule ? api.put(`/productivity/rules/${rule.id}`, body) : api.post('/productivity/rules', body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['productivity'] }); onClose(); },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={rule ? t('Edit app or website') : t('Add app or website')}
      footer={<><Button variant="secondary" onClick={onClose}>{t('Cancel')}</Button><Button loading={save.isPending} disabled={!f.pattern.trim()} onClick={() => save.mutate()}>{t('Save')}</Button></>}
    >
      <ErrorBanner error={save.error} />
      <div className="space-y-4">
        <Field label={t('Type')}>
          <Select value={f.matchType} onChange={(e) => setF({ ...f, matchType: e.target.value as typeof f.matchType })}>
            <option value="app">{t('Application (program file, e.g. revit.exe)')}</option>
            <option value="domain">{t('Website (e.g. youtube.com — includes its subdomains)')}</option>
            <option value="title">{t('Window title contains… (only if titles are recorded)')}</option>
          </Select>
        </Field>
        <Field label={f.matchType === 'domain' ? t('Website') : f.matchType === 'app' ? t('Program file') : t('Text')}>
          <Input value={f.pattern} onChange={(e) => setF({ ...f, pattern: e.target.value })} placeholder={f.matchType === 'domain' ? 'youtube.com' : f.matchType === 'app' ? 'revit.exe' : t('invoice')} />
        </Field>
        <Field label={t('Display name (optional)')}><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Autodesk Revit" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Counts as')}>
            <Select value={f.classification} onChange={(e) => setF({ ...f, classification: e.target.value as ProductivityClass })}>
              <option value="productive">{t('Productive')}</option>
              <option value="neutral">{t('Neutral')}</option>
              <option value="unproductive">{t('Unproductive')}</option>
            </Select>
          </Field>
          <Field label={t('Applies to')}>
            <Select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}>
              <option value="">{t('Everyone')}</option>
              {!!depts.data?.length && (
                <optgroup label={t('A department')}>
                  {depts.data.map((d) => <option key={d.id} value={`d:${d.id}`}>{t('{name} only', { name: d.name })}</option>)}
                </optgroup>
              )}
              {!!people.data?.length && (
                <optgroup label={t('One person (exception)')}>
                  {people.data.map((p) => <option key={p.id} value={`e:${p.id}`}>{t('{name} only', { name: p.fullName })}</option>)}
                </optgroup>
              )}
            </Select>
          </Field>
        </div>
        {f.scope.startsWith('e:') && (
          <p className="text-xs text-slate-500">{t('A personal exception wins over the department and company rules for this person only — e.g. YouTube counts as productive for someone who watches Revit tutorials.')}</p>
        )}
        <Field label={t('Category (optional)')}>
          <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} list="rule-categories" maxLength={60} placeholder={t('Engineering & design')} />
          <datalist id="rule-categories">{categoryList.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label={t('Note (optional)')}><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={300} /></Field>
      </div>
    </Modal>
  );
}

function DevicesTab() {
  const qc = useQueryClient();
  const { fmt } = useTz();
  const q = useQuery({ queryKey: ['productivity', 'devices'], queryFn: () => api.get<AgentDeviceDto[]>('/productivity/devices') });
  const opts = useEmployeeOptions();
  const [employeeId, setEmployeeId] = useState('');
  const [code, setCode] = useState<{ code: string; expiresAt: string; name: string } | null>(null);
  const [revoking, setRevoking] = useState<AgentDeviceDto | null>(null);
  const create = useMutation({
    mutationFn: () => api.post<{ code: string; expiresAt: string }>('/productivity/devices/codes', { employeeId }),
    onSuccess: (r) => setCode({ ...r, name: opts.data?.find((o) => o.id === employeeId)?.fullName ?? '' }),
  });
  const downloads = useQuery({ queryKey: ['meta', 'downloads'], queryFn: () => api.get<{ name: string; size: number; updatedAt: string }[]>('/meta/downloads') });
  const installer = downloads.data?.filter((d) => /\.(exe|msi)$/i.test(d.name)).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  const extension = downloads.data?.find((d) => /extension/i.test(d.name) && d.name.endsWith('.zip'));
  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/productivity/devices/${id}/revoke`, {}),
    onSuccess: () => { setRevoking(null); qc.invalidateQueries({ queryKey: ['productivity'] }); },
  });
  const noInstaller = t('The installer has not been placed on the server yet (folder {folder}).').split('{folder}');
  return (
    <div className="space-y-4">
      <Card title={t('1. Install the desktop agent (Windows)')}>
        {downloads.isLoading ? (
          <Spinner />
        ) : installer ? (
          <div className="flex flex-wrap items-center gap-3">
            <a href={`/downloads/${encodeURIComponent(installer.name)}`} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
              <Download className="h-4 w-4" /> {t('Download installer')}
            </a>
            <span className="text-xs text-slate-500">{installer.name} · {(installer.size / 1e6).toFixed(0)} MB</span>
            {extension && (
              <a href={`/downloads/${encodeURIComponent(extension.name)}`} className="text-sm font-medium text-brand-700 hover:underline">
                {t('Browser extension (optional)')}
              </a>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500">{noInstaller[0]}<code>apps/api/downloads</code>{noInstaller[1]}</p>
        )}
        <p className="mt-3 text-xs text-slate-500">
          {t("Install it on the employee's laptop — no administrator rights needed. It starts with Windows and shows an icon next to the clock. The optional Chrome/Edge extension gives exact website names; without it common sites (YouTube, Facebook, Gmail…) are still recognised.")}
        </p>
      </Card>
      <Card title={t('2. Connect the computer')}>
        <ErrorBanner error={create.error} />
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('Employee')} className="min-w-64">
            <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">{t('Choose…')}</option>
              {opts.data?.map((o) => <option key={o.id} value={o.id}>{o.fullName}</option>)}
            </Select>
          </Field>
          <Button disabled={!employeeId} loading={create.isPending} onClick={() => create.mutate()}>{t('Get connection code')}</Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          {t('On first start the agent asks for the server address and this one-time code. The employee then sees the monitoring notice and must accept it before anything is recorded.')}
        </p>
      </Card>
      <ErrorBanner error={q.error} />
      <Card bodyClass="p-0" title={t('Connected computers')}>
        {q.isLoading ? (
          <Spinner />
        ) : q.data?.length ? (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">{t('Employee')}</th>
                <th className="px-4 py-2">{t('Computer')}</th>
                <th className="hidden px-4 py-2 md:table-cell">{t('Connected')}</th>
                <th className="px-4 py-2">{t('Last contact')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {q.data.map((d) => (
                <tr key={d.id} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-medium">{d.employeeName}</td>
                  <td className="px-4 py-2">{d.hostname}<p className="text-xs text-slate-400">{[d.os, d.agentVersion && t('agent {version}', { version: d.agentVersion })].filter(Boolean).join(' · ')}</p></td>
                  <td className="hidden px-4 py-2 text-slate-500 md:table-cell">{fmt(d.enrolledAt, null, 'dd LLL yyyy')}</td>
                  <td className="px-4 py-2">{d.revokedAt ? <Badge>{t('Disconnected')}</Badge> : d.lastSeenAt ? fmt(d.lastSeenAt, null, 'dd LLL, HH:mm') : t('Never')}</td>
                  <td className="px-4 py-2 text-end">{!d.revokedAt && <Button size="sm" variant="ghost" onClick={() => setRevoking(d)}>{t('Disconnect')}</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState icon={<Laptop className="h-6 w-6" />} title={t('No computers connected yet')} />
        )}
      </Card>
      <Modal open={!!code} onClose={() => setCode(null)} title={t('Connection code for {name}', { name: code?.name ?? '' })} size="sm" footer={<Button onClick={() => setCode(null)}>{t('Done')}</Button>}>
        {code && (
          <div className="space-y-3 text-center">
            <p className="font-mono text-3xl font-bold tracking-widest text-slate-900">{code.code}</p>
            <Button variant="secondary" size="sm" icon={<Copy className="h-3.5 w-3.5" />} onClick={() => void navigator.clipboard?.writeText(code.code)}>{t('Copy')}</Button>
            <p className="text-sm text-slate-500">{t('Works once, until {time}.', { time: DateTime.fromISO(code.expiresAt).toFormat('dd LLL, HH:mm') })}</p>
            <p className="text-xs text-slate-500">{t('Server address:')} <span className="font-mono">{window.location.origin}</span></p>
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
        title={t('Disconnect this computer?')}
        message={t('{name} stops sending data immediately. Its past data is kept.', { name: revoking?.hostname })}
        confirmLabel={t('Disconnect')}
        danger
        loading={revoke.isPending}
        error={revoke.error}
      />
    </div>
  );
}
